/**
 * Template HTML pour la génération de factures
 * Ce template est utilisé pour créer une facture HTML qui sera ensuite convertie en PDF
 */
import API_CONFIG from '../../config/api.js';
import { INVOICE_AMOUNT_DECLARATION, invoiceAmountInWords } from '../../utils/invoiceAmountInWords';
import { getInvoicePageLayout, installInvoicePrintPagination } from '../../utils/invoicePrintPagination';
import { isInvoiceQrEnabled } from '../../utils/invoiceQrVisibility';
import { normalizeInvoiceSubject } from '../../utils/invoiceSubject';

export const generateInvoiceHTML = (data: any): string => {
  const {
    order,
    clientObj,
    warehouse,
    paymentStatus,
    montantVerse,
    montantDu,
    totals,
    formattedDate,
    formatNumber,
  } = data;

  const qrCodeDataUrl = data.qrCodeDataUrl || data.qrCode || "";
  const isProforma = order.isProforma === true || order.documentType === "PROFORMA";
  const invoiceSubject = !isProforma && !order.isPurchaseOrder &&
    !["CREDIT_NOTE", "RETURN_SUMMARY"].includes(order.documentType) &&
    (!order.order_type || String(order.order_type).toLowerCase() === "sales")
    ? normalizeInvoiceSubject(order.invoice_subject) : null;
  const invoiceSubjectEntities: Record<string, string> = {
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  };
  const escapedInvoiceSubject = invoiceSubject ? invoiceSubject.replace(/[&<>"']/g,
    (character: string) => invoiceSubjectEntities[character]) : "";
  const toNumberWithDecimalSupport = (value: any): number => {
    if (value === null || value === undefined || value === "") return 0;
    const normalized =
      typeof value === "string"
        ? value.replace(/\s/g, "").replace(/,/g, ".")
        : value;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const formatQuantity = (value: any): string =>
    toNumberWithDecimalSupport(value).toLocaleString("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 5,
    });

  // **AJOUT DE LOGS POUR DIAGNOSTIC DES POLICES**
  console.log(
    "🔍 [FONT DEBUG] Début génération facture avec diagnostic polices"
  );
  console.log(
    "🔍 [FONT DEBUG] Environnement:",
    typeof window !== "undefined" ? "client" : "serveur"
  );

  // Détection de l'environnement et construction des URLs de polices
  const getBaseUrl = () => {
    if (typeof window !== "undefined") {
      console.log("🔍 [FONT DEBUG] URL base (client):", window.location.origin);
      return window.location.origin;
    }

    // En production, utiliser la variable d'environnement ou une URL par défaut
    const baseUrl = API_CONFIG.BASE_URL;
    console.log("🔍 [FONT DEBUG] URL base (serveur):", baseUrl);
    return baseUrl;
  };

  const baseUrl = getBaseUrl();

  // **CSS OPTIMISÉ AVEC CHEMINS MULTIPLES ET FALLBACKS ROBUSTES**
  const fontCSS = `
    /* Polices Inter optimisées pour PDF avec fallbacks robustes */
    @font-face {
      font-family: "Inter";
      src: url("${baseUrl}/fonts/Inter-Light-subset.woff2") format("woff2"),
           url("${baseUrl}/public/fonts/Inter-Light-subset.woff2") format("woff2"),
           url("./fonts/Inter-Light-subset.woff2") format("woff2"),
           url("/fonts/Inter-Light-subset.woff2") format("woff2");
      font-weight: 300;
      font-style: normal;
      font-display: block;
      unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
    }
    @font-face {
      font-family: "Inter";
      src: url("${baseUrl}/fonts/Inter-Regular-subset.woff2") format("woff2"),
           url("${baseUrl}/public/fonts/Inter-Regular-subset.woff2") format("woff2"),
           url("./fonts/Inter-Regular-subset.woff2") format("woff2"),
           url("/fonts/Inter-Regular-subset.woff2") format("woff2");
      font-weight: 400;
      font-style: normal;
      font-display: block;
      unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
    }
    @font-face {
      font-family: "Inter";
      src: url("${baseUrl}/fonts/Inter-Medium-subset.woff2") format("woff2"),
           url("${baseUrl}/public/fonts/Inter-Medium-subset.woff2") format("woff2"),
           url("./fonts/Inter-Medium-subset.woff2") format("woff2"),
           url("/fonts/Inter-Medium-subset.woff2") format("woff2");
      font-weight: 500;
      font-style: normal;
      font-display: block;
      unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
    }
    @font-face {
      font-family: "Inter";
      src: url("${baseUrl}/fonts/Inter-SemiBold-subset.woff2") format("woff2"),
           url("${baseUrl}/public/fonts/Inter-SemiBold-subset.woff2") format("woff2"),
           url("./fonts/Inter-SemiBold-subset.woff2") format("woff2"),
           url("/fonts/Inter-SemiBold-subset.woff2") format("woff2");
      font-weight: 600;
      font-style: normal;
      font-display: block;
      unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
    }
    @font-face {
      font-family: "Inter";
      src: url("${baseUrl}/fonts/Inter-Bold-subset.woff2") format("woff2"),
           url("${baseUrl}/public/fonts/Inter-Bold-subset.woff2") format("woff2"),
           url("./fonts/Inter-Bold-subset.woff2") format("woff2"),
           url("/fonts/Inter-Bold-subset.woff2") format("woff2");
      font-weight: 700;
      font-style: normal;
      font-display: block;
      unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
    }
    
    /* Fallback sécurisé pour tous les éléments */
    * {
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
    }
  `;

  console.log("🔍 [FONT DEBUG] CSS des polices généré:", fontCSS);

  // Signature plateforme utilisée si aucun logo d'entreprise n'est disponible.
  const defaultLogoBase64 = "/brand/elsa/logo-horizontal-color-web.png";

  // Couleur d'accentuation principale
  const primaryColor = "#2c5282"; // Même bleu que le template React-PDF.
  const secondaryColor = "#e2e8f0"; // Gris clair pour les arrière-plans
  const successColor = "#38a169"; // Vert pour "payé"
  const warningColor = "#d69e2e"; // Orange pour "partiellement payé"
  const dangerColor = "#e53e3e"; // Rouge pour "non payé"

  // **LOGS DE DIAGNOSTIC POUR LES LOGOS**
  console.log("🖼️ [LOGO DEBUG] Informations du warehouse:", {
    name: warehouse?.name,
    logo: warehouse?.logo,
    hasLogo: !!warehouse?.logo,
  });

  // Vérification et préparation du logo
  let logoHtml = "";
  if (warehouse?.logo) {
    // Vérifier si le logo est un URL ou une chaîne base64
    const isBase64 = warehouse.logo.includes("data:image");
    console.log("🖼️ [LOGO DEBUG] Logo détecté comme base64:", isBase64);

    // Correction de l'URL du logo pour s'assurer qu'il pointe vers le bon chemin
    let logoUrl = "";
    let logoPath = warehouse.logo;

    // Nettoyage du chemin (supprimer les barres obliques en début si présentes)
    if (logoPath.startsWith("/")) {
      logoPath = logoPath.substring(1);
    }

    if (isBase64) {
      logoUrl = warehouse.logo;
      console.log("🖼️ [LOGO DEBUG] Utilisation logo base64 direct");
    } else {
      // **CONSTRUCTION ROBUSTE DE L'URL POUR TOUS LES ENVIRONNEMENTS**
      logoUrl = `${baseUrl}/${logoPath}`;
      console.log("🖼️ [LOGO DEBUG] URL construite:", logoUrl);
    }

    console.log("🖼️ [LOGO DEBUG] Logo original:", warehouse.logo);
    console.log("🖼️ [LOGO DEBUG] Logo path nettoyé:", logoPath);
    console.log("🖼️ [LOGO DEBUG] URL finale du logo:", logoUrl);

    // **GESTION ROBUSTE DES ERREURS AVEC FALLBACKS MULTIPLES**
    logoHtml = `
      <div class="invoice-logo-container">
        <img 
          src="${logoUrl}" 
          alt="${warehouse?.name || "ELSA Technologies"}" 
          style="max-height: 120px; width: auto;"
          crossorigin="anonymous"
          onload="console.log('🖼️ [LOGO DEBUG] Logo chargé avec succès:', this.src)"
          onerror="
            console.error('🖼️ [LOGO DEBUG] Erreur chargement logo:', this.src);
            const attempts = this.getAttribute('data-attempts') || '0';
            const attemptNum = parseInt(attempts) + 1;
            this.setAttribute('data-attempts', attemptNum.toString());
            
            if (attemptNum === 1) {
              // Tentative 1: URL absolue avec baseUrl
              const newUrl = '${baseUrl}/${logoPath}';
              console.log('🖼️ [LOGO DEBUG] Tentative 1 - URL absolue:', newUrl);
              this.src = newUrl;
            } else if (attemptNum === 2) {
              // Tentative 2: Chemin direct depuis public
              const directUrl = '${baseUrl}/warehouses/${logoPath
      .split("/")
      .pop()}';
              console.log('🖼️ [LOGO DEBUG] Tentative 2 - Chemin direct:', directUrl);
              this.src = directUrl;
            } else if (attemptNum === 3) {
              // Tentative 3: API endpoint
              const apiUrl = '${baseUrl}/api/warehouses/logo/${logoPath
      .split("/")
      .pop()}';
              console.log('🖼️ [LOGO DEBUG] Tentative 3 - API endpoint:', apiUrl);
              this.src = apiUrl;
            } else {
              // Fallback final: Logo par défaut en base64
              console.log('🖼️ [LOGO DEBUG] Toutes tentatives échouées, utilisation logo par défaut');
              this.onerror = null;
              this.src = '${defaultLogoBase64}';
              this.alt = 'ELSA Technologies Logo';
            }
          "
        >
      </div>
    `;
  } else {
    console.log(
      "🖼️ [LOGO DEBUG] Aucun logo d'établissement trouvé, utilisation du logo par défaut"
    );
    console.log("🖼️ [LOGO DEBUG] Warehouse complet:", warehouse);

    // **LOGO PAR DÉFAUT SÉCURISÉ EN BASE64**
    logoHtml = `
      <div class="invoice-logo-container">
        <img 
          src="${defaultLogoBase64}" 
          alt="ELSA Technologies Logo" 
          style="max-height: 120px; width: auto;"
          onload="console.log('🖼️ [LOGO DEBUG] Logo par défaut chargé avec succès')"
          onerror="console.error('🖼️ [LOGO DEBUG] Erreur critique: échec chargement logo par défaut')"
        >
      </div>
    `;
  }

  // Helper function to format legal information
  const formatLegalInfo = (warehouse: any): string => {
    const legalFields = [];

    if (warehouse?.website) {
      legalFields.push(
        `<div style="margin-top: 3px;"><strong>Site web:</strong> <a href="${warehouse.website}" target="_blank" style="color: ${primaryColor}; text-decoration: none;">${warehouse.website}</a></div>`
      );
    }

    if (warehouse?.tax_number) {
      legalFields.push(
        `<div style="margin-top: 3px;"><strong>N° IFU/TVA:</strong> ${warehouse.tax_number}</div>`
      );
    }

    if (warehouse?.rccm_number) {
      legalFields.push(
        `<div style="margin-top: 3px;"><strong>N° RCCM:</strong> ${warehouse.rccm_number}</div>`
      );
    }

    if (warehouse?.registration_number) {
      legalFields.push(
        `<div style="margin-top: 3px;"><strong>N° RC:</strong> ${warehouse.registration_number}</div>`
      );
    }

    if (warehouse?.legal_status) {
      legalFields.push(
        `<div style="margin-top: 3px;"><strong>Forme juridique:</strong> ${warehouse.legal_status}</div>`
      );
    }

    if (warehouse?.capital_social) {
      legalFields.push(
        `<div style="margin-top: 3px;"><strong>Capital:</strong> ${warehouse.capital_social}</div>`
      );
    }

    return legalFields.length > 0
      ? `<div class="legal-info">${legalFields.join("")}</div>`
      : "";
  };

  // Produits vendus
  const produitsVendusHTML = (order.produitsVendus || [])
    .map((item: any, index: number) => {
      const quantity = isProforma
        ? Number(item.quantity ?? item.quantite) || 1
        : toNumberWithDecimalSupport(item.quantity ?? item.quantite);
      const unitPrice = Number(item.unit_price ?? item.prix_unitaire_HT) || 0;
      const lineDiscount = Number(item.discount_rate ?? item.remise) || 0;
      const lineTotal = unitPrice * quantity;

      return `
      <tr class="${index % 2 === 0 ? "item-even" : "item-odd"}">
        <td style="border-left: 1px solid #e2e8f0; text-align: center;">${
          index + 1
        }</td>
        <td>${item.product_name || "Produit inconnu"}</td>
        <td style="text-align: center;">${isProforma ? formatNumber(quantity) : formatQuantity(quantity)} ${
          item.unit_short_name || ""
        }</td>
        <td style="text-align: right;">${formatNumber(unitPrice)}</td>
        <td style="text-align: right;">${formatNumber(lineDiscount)}</td>
        <td style="text-align: right; font-weight: 500;">${formatNumber(
          lineTotal
        )}</td>
      </tr>
    `
    })
    .join("");

  // Paiements
  const paymentsHTML = (order.payments || [])
    .map(
      (payment: any, index: number) => `
      <span style="display:block; margin-top: 5px; padding: 5px; background-color: ${secondaryColor}; border-radius: 4px;">
        ${index + 1}. ${
        payment.date
          ? new Date(payment.date).toLocaleDateString("fr-FR")
          : "N/A"
      } - <strong>${
        payment.payment_mode_name || "Mode non spécifié"
      }</strong> - ${formatNumber(
        payment.amount || payment.montant_affecte || 0
      )} CFA
      </span>
    `
    )
    .join("");

  // Statut de paiement avec couleur
  let statusColor = dangerColor; // Rouge par défaut (non payé)
  let statusText = "Non payé";
  let statusBgColor = "#FEE2E2"; // Fond léger rouge

  if (
    paymentStatus &&
    (paymentStatus.toLowerCase() === "paid" ||
      paymentStatus.toLowerCase() === "payé")
  ) {
    statusColor = successColor; // Vert pour payé
    statusText = "Payé";
    statusBgColor = "#DEF7EC"; // Fond léger vert
  } else if (
    paymentStatus &&
    (paymentStatus.toLowerCase().includes("partial") ||
      paymentStatus.toLowerCase().includes("partiel"))
  ) {
    statusColor = warningColor; // Orange pour partiellement payé
    statusText = "Partiellement payé";
    statusBgColor = "#FEF3C7"; // Fond léger orange
  }
  if (isProforma) {
    statusColor = primaryColor;
    statusText = "Proforma";
    statusBgColor = "#E6F0FF";
  }

  const issuerName = [warehouse?.name || "Entreprise", warehouse?.legal_status]
    .filter(Boolean).join(" - ");
  const parsedDueDate = order.due_date ? new Date(order.due_date) : null;
  const dueDate = parsedDueDate && Number.isFinite(parsedDueDate.getTime())
    ? parsedDueDate.toLocaleDateString("fr-FR")
    : "À préciser";
  const paymentTerms = order.payment_terms || "À préciser";
  const paymentRowsHTML = (isProforma ? [
    ["Échéance envisagée :", dueDate],
    ["Modalités de paiement :", paymentTerms],
  ] : [
    ["Montant versé :", formatNumber(montantVerse) + " CFA"],
    ["Reste à payer :", formatNumber(montantDu) + " CFA"],
    ["Échéance :", dueDate],
    ["Modalités de paiement :", paymentTerms],
  ]).map(([label, value]) =>
    '<div class="payment-row"><span class="payment-label">' + label +
    '</span><span class="payment-value">' + value + '</span></div>'
  ).join("");
  const footerContacts = [
    warehouse?.phone ? "Tél : " + warehouse.phone : "",
    warehouse?.email ? "Email : " + warehouse.email : "",
  ].filter(Boolean).join(" ; ");
  const documentReference = (isProforma ? "Proforma" : "Facture") + " n° " + (order.invoice_number || "");
  const footerLineCount = [issuerName, documentReference, warehouse?.address, footerContacts,
    warehouse?.website, warehouse?.tax_number, warehouse?.rccm_number,
    warehouse?.registration_number, warehouse?.legal_status, warehouse?.capital_social,
    "Facturé par : " + (order.created_by_name || "SysAdmin") + " ; Imprimé par : " + (order.printed_by_name || "N/A"),
  ].filter(Boolean).reduce((count, line) => count + Math.max(1, Math.ceil(String(line).length / 105)), 0);
  const showInvoiceQr = isProforma || isInvoiceQrEnabled(warehouse);
  const hasVerificationQr = showInvoiceQr && Boolean(qrCodeDataUrl || (order.invoice_number && order.verification_token));
  const amountInWords = isProforma ? "" : invoiceAmountInWords(formatNumber(totals.total), "CFA");
  const amountDeclarationHTML = amountInWords
    ? '<div class="invoice-amount-words"><div>' + INVOICE_AMOUNT_DECLARATION + '</div><strong>' + amountInWords + '</strong></div>'
    : "";
  const footerReservedMm = (48 + footerLineCount * 12 + (hasVerificationQr ? 100 : 0)) * 25.4 / 72;
  const qrHTML = showInvoiceQr && qrCodeDataUrl
    ? '<div class="qr-code-block"><img src="' + qrCodeDataUrl +
      '" alt="QR code de vérification" /><div class="qr-code-label">Vérifier cette ' + (isProforma ? 'proforma' : 'facture') + '</div></div>'
    : "";
  const additionalSectionsHTML = [
    paymentsHTML ? '<section class="additional-section"><div class="section-title">Détails des paiements</div>' + paymentsHTML + '</section>' : "",
    order.notes ? '<section class="additional-section"><div class="section-title">Remarques</div><div>' + order.notes + '</div></section>' : "",
    warehouse?.terms_condition ? '<section class="additional-section"><div class="section-title">Termes et conditions</div><div>' + warehouse.terms_condition + '</div></section>' : "",
    warehouse?.bank_details ? '<section class="additional-section"><div class="section-title">Détails de banque</div><div>' + warehouse.bank_details + '</div></section>' : "",
  ].join("");

  // Même hiérarchie que le PDF, sur une page blanche indépendante du thème.
  return `
    <!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="utf-8">
      <meta name="color-scheme" content="light">
      <title>${isProforma ? "Proforma" : "Facture"} ${order.invoice_number || ""}</title>
      <style>
        ${fontCSS}
        @page {
          size: A4; margin: 10mm;
          @bottom-right {
            content: "Page " counter(page) " sur " counter(pages);
            font-family: Arial, sans-serif; font-size: 8pt; color: #738099;
          }
        }
        body {
          margin: 0; padding: 0; background: #ffffff; color: #233f63;
          color-scheme: light; font-size: 14px; line-height: 1.3;
        }
        .invoice-box {
          box-sizing: border-box; width: 210mm; min-height: 297mm;
          margin: auto; padding: 10mm; background: #ffffff;
          display: flex; flex-direction: column; font-size: 14px;
        }
        .document-layout { width: 100%; border-collapse: collapse; }
        .document-layout-cell { padding: 0; }
        .document-footer-space { display: none; }
        .invoice-content { display: flow-root; }
        ${!isProforma ? '.invoice-content > :last-child { margin-bottom: 0; }' : ''}
        .header {
          display: flex; justify-content: space-between; align-items: center;
          padding-bottom: ${isProforma ? 24 : 16}px; margin-bottom: ${isProforma ? 32 : 8}px;
          border-bottom: 1px solid #92afd4;
        }
        .company-info {
          width: 55%; display: flex; align-items: center; min-width: 0;
        }
        .invoice-logo-container {
          width: ${isProforma ? 112 : 96}px; height: ${isProforma ? 112 : 96}px; flex: 0 0 ${isProforma ? 112 : 96}px;
          display: flex; align-items: center; justify-content: center;
          margin-right: 5px;
        }
        .invoice-logo-container img {
          max-height: ${isProforma ? 112 : 96}px !important; max-width: ${isProforma ? 112 : 96}px !important;
          width: auto; height: auto; object-fit: contain;
        }
        .issuer-name { font-size: 18.5px; font-weight: 700; color: ${primaryColor}; }
        .invoice-info { width: 43%; text-align: right; }
        .title { font-size: 29px; line-height: 1.15; font-weight: 700; color: ${primaryColor}; margin-bottom: 12px; }
        .document-detail { font-size: 12.5px; margin-bottom: 5px; }
        .document-detail strong { font-weight: 600; }
        .recipient { margin-bottom: ${isProforma ? 32 : 8}px; break-inside: avoid; }
        .recipient-heading {
          background: ${primaryColor}; color: #ffffff;
          padding: ${invoiceSubject ? 5 : 8}px 13px; font-weight: 700;
        }
        .recipient-name { font-size: 21px; font-weight: 700; margin: ${isProforma ? 16 : 11}px 10px; }
        .recipient-details { display: flex; padding: 0 10px; }
        .recipient-column { box-sizing: border-box; width: 50%; overflow-wrap: anywhere; }
        .recipient-column:first-child { padding-right: 21px; }
        .recipient-column:last-child { padding-left: 21px; border-left: 1px solid #92afd4; }
        .recipient-address { margin-top: ${invoiceSubject ? 0 : 5}px; padding: 0 10px; overflow-wrap: anywhere; }
        .invoice-subject { margin-top: 8px; padding: 0 10px; overflow-wrap: anywhere; }
        .contact-line { margin-bottom: 5px; }
        .products-table {
          width: 100%; table-layout: fixed; border-collapse: collapse;
          margin: 0; font-variant-numeric: tabular-nums;
        }
        .products-table th {
          background: ${primaryColor}; color: #ffffff; padding: ${isProforma ? 12 : invoiceSubject ? 6 : 9}px 5px;
          font-size: 14px; font-weight: 700; text-align: left;
        }
        .products-table td { padding: ${isProforma ? 11 : 8}px 5px; border-bottom: 1px solid ${secondaryColor}; overflow-wrap: anywhere; }
        .products-table thead { display: table-header-group; }
        .products-table tr { break-inside: avoid; }
        .item-even { background: #f1f6fc; }
        .currency-caption { font-size: 11.5px; color: #61799c; margin: 7px 0 ${isProforma ? 27 : 5}px; }
        .payment-status { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: ${isProforma ? 19 : 11}px; break-inside: avoid; }
        .payment-info, .totals { box-sizing: border-box; width: 49%; border: 1px solid ${secondaryColor}; border-radius: 5px; }
        .payment-info { padding: ${isProforma ? 16 : 8}px; }
        .payment-heading { display: flex; flex-wrap: wrap; align-items: center; margin-bottom: ${isProforma ? 16 : 8}px; gap: 16px; }
        .section-title { color: ${primaryColor}; font-weight: 700; }
        .payment-heading .section-title { font-size: 17px; }
        .status-badge { display: inline-block; padding: 6px 13px; border-radius: 5px; font-weight: 700; }
        .payment-row { display: flex; align-items: flex-start; margin-bottom: ${isProforma ? 11 : 4}px; }
        .payment-label { box-sizing: border-box; width: 53%; font-weight: 600; padding-right: 8px; font-size: 13px; }
        .payment-value { width: 47%; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
        .totals { overflow: hidden; }
        .totals-table { width: 100%; border-collapse: collapse; font-variant-numeric: tabular-nums; }
        .totals-table td { padding: ${isProforma ? 11 : 4}px 14px; border-bottom: 1px solid ${secondaryColor}; }
        .totals-label { width: 59%; font-size: 13px; }
        .totals-value { text-align: right; white-space: nowrap; }
        .totals-table .net-total td { padding: ${isProforma ? 19 : 14}px 14px; border-bottom: 0; background: ${primaryColor}; color: #ffffff; font-weight: 700; }
        .net-amount { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
        .net-label { font-size: 18px; }
        .net-value { font-size: 21px; white-space: nowrap; }
        .additional-section { margin-top: 12px; padding: 13px; border: 1px solid ${secondaryColor}; border-radius: 5px; break-inside: avoid; overflow-wrap: anywhere; }
        .additional-section .section-title { margin-bottom: 6px; }
        .footer { margin-top: auto; padding-top: 32px; break-inside: avoid; text-align: center; }
        .invoice-amount-words { padding-bottom: 24px; color: ${primaryColor}; font-size: 14px; line-height: 1.4; text-align: center; }
        .invoice-amount-words strong { font-weight: 600; }
        .invoice-print-pages { display: none; }
        .qr-code-block { width: ${isProforma ? 140 : 110}px; margin: 0 auto 24px; }
        .qr-code-block img { display: block; width: 94px; height: 94px; margin: auto; object-fit: contain; }
        .qr-code-label { font-size: 11.5px; margin-top: 7px; color: ${primaryColor}; }
        .establishment-footer { border-top: 1px solid #92afd4; padding-top: 11px; font-size: 11.5px; overflow-wrap: anywhere; }
        .footer-name { color: ${primaryColor}; font-size: 12px; font-weight: 700; }
        .footer-line { margin-bottom: 3px; }
        .legal-info { display: flex; flex-wrap: wrap; justify-content: center; gap: 3px 12px; }
        .legal-info a { color: inherit; text-decoration: none; }
        @media print {
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .invoice-box { width: auto; min-height: 0; padding: 0; display: block; }
          .document-footer-space { display: table-footer-group; }
          /* Chromium ne répète pas un tfoot dépassant le quart de la page. */
          .footer-spacer { height: ${Math.max(10, Math.min(footerReservedMm - 16, 68))}mm; }
          .footer { position: fixed; bottom: 0; left: 0; right: 0; margin: 0; padding-top: 0; }
          .footer .qr-code-block { margin-bottom: 12px; }
          .establishment-footer { padding-top: 8px; font-size: 11px; line-height: 1.15; }
          .footer-name { font-size: 11.5px; }
          .footer-line { margin-bottom: 2px; }
          .legal-info { gap: 2px 12px; }
          .legal-info div { margin-top: 1px !important; }
          ${!isProforma ? `
          .invoice-box { width: 190mm; }
          .invoice-box > .footer { position: static; }
          .document-footer-space { display: none; }
          .invoice-is-paginated > .invoice-box { display: none; }
          .invoice-print-pages { display: block; width: 190mm; }
          .invoice-print-page { position: relative; height: 277mm; break-after: page; overflow: hidden; }
          .invoice-print-page:last-child { break-after: auto; }
          .invoice-print-content { position: absolute; left: 0; right: 0; overflow: hidden; }
          /* Empêcher Chromium de repaginer le tableau à l'intérieur de chaque découpe. */
          .invoice-print-content > .invoice-content { display: inline-block; width: 100%; vertical-align: top; }
          .invoice-print-table-header { position: absolute; top: 0; left: 0; width: 100%; margin: 0; }
          .invoice-print-footer { position: absolute; bottom: 0; left: 0; right: 0; text-align: center; }
          .invoice-print-footer .qr-code-block { margin-bottom: 12px; }
          .invoice-print-declaration { position: absolute; left: 0; right: 0; }
          ` : ""}
        }
      </style>
    </head>
    <body>
      <div class="invoice-box">
        <table class="document-layout" role="presentation"><tbody><tr><td class="document-layout-cell">
        <main class="invoice-content">
          <div class="header">
            <div class="company-info">
              ${logoHtml}
              <div class="issuer-name">${issuerName}</div>
            </div>
            <div class="invoice-info">
              <div class="title">${isProforma ? "PROFORMA" : "Facture de vente"}</div>
              <div class="document-detail"><strong>N° :</strong> ${order.invoice_number || ""}</div>
              <div class="document-detail"><strong>Date d'émission :</strong> ${formattedDate || ""}</div>
              <div class="document-detail"><strong>${isProforma ? "Statut de la proforma" : "Statut de la vente"} :</strong> ${order.order_status || "En traitement"}</div>
            </div>
          </div>
          <section class="recipient">
            <div class="recipient-heading">${isProforma ? "DESTINATAIRE" : "FACTURÉ À"}</div>
            <div class="recipient-name">${clientObj?.name || "Client inconnu"}</div>
            <div class="recipient-details">
              <div class="recipient-column">
                <div class="contact-line">Tél : ${clientObj?.phone || "Non renseigné"}</div>
                <div class="contact-line">Email : ${clientObj?.email || "Non renseigné"}</div>
              </div>
              <div class="recipient-column">
                ${clientObj?.ifu || clientObj?.tax_number ? '<div class="contact-line">N°IFU : ' + (clientObj.ifu || clientObj.tax_number) + '</div>' : ""}
                ${clientObj?.rccm ? '<div class="contact-line">RCCM : ' + clientObj.rccm + '</div>' : ""}
                ${isProforma && clientObj?.address ? '<div class="contact-line">Adresse : ' + clientObj.address + '</div>' : ""}
              </div>
            </div>
            ${!isProforma && clientObj?.address ? '<div class="recipient-address">Adresse : ' + clientObj.address + '</div>' : ""}
            <!-- invoice-subject:start -->${invoiceSubject ? '<div class="invoice-subject"><strong>Objet :</strong> ' + escapedInvoiceSubject + '</div>' : ""}<!-- invoice-subject:end -->
          </section>
          <table class="products-table">
            <thead><tr>
              <th style="width: 5%; text-align: center;">#</th>
              <th style="width: 35%;">Désignation</th>
              <th style="width: 15%; text-align: center;">Quantité</th>
              <th style="width: 15%; text-align: right;">Prix unitaire</th>
              <th style="width: 15%; text-align: right;">Remise</th>
              <th style="width: 15%; text-align: right;">Total brut</th>
            </tr></thead>
            <tbody>${produitsVendusHTML}</tbody>
          </table>
          <div class="currency-caption">Montants en CFA</div>
          <div class="payment-status">
            <div class="payment-info">
              <div class="payment-heading">
                <div class="section-title">${isProforma ? "CONDITIONS" : "PAIEMENT"}</div>
                <span class="status-badge" style="background-color: ${statusBgColor}; color: ${statusColor};">${statusText}</span>
              </div>
              ${paymentRowsHTML}
            </div>
            <div class="totals">
              <table class="totals-table">
                <tr><td class="totals-label">Sous-total avant remise</td><td class="totals-value">${formatNumber(totals.subtotal)} CFA</td></tr>
                <tr><td class="totals-label">Remise totale</td><td class="totals-value">${totals.totalDiscount > 0 ? "-" : ""}${formatNumber(totals.totalDiscount)} CFA</td></tr>
                <tr><td class="totals-label">Taxe</td><td class="totals-value">${formatNumber(totals.totalTax)} CFA</td></tr>
                <tr class="net-total"><td colspan="2"><div class="net-amount"><span class="net-label">${isProforma ? "Total TTC" : "Net à payer"}</span><span class="net-value">${formatNumber(totals.total)} CFA</span></div></td></tr>
              </table>
            </div>
          </div>
          ${isProforma ? '<section class="additional-section"><div class="section-title">Document proforma</div><div>Cette proforma n\'est pas une facture et ne constitue pas une demande de paiement.</div></section>' : ''}
          ${additionalSectionsHTML}
        </main>
        </td></tr></tbody><tfoot class="document-footer-space"><tr><td class="document-layout-cell"><div class="footer-spacer"></div></td></tr></tfoot></table>
        <footer class="footer">
          ${amountDeclarationHTML}
          <div class="invoice-footer-body">
          <!-- document-verification:start -->${qrHTML}<!-- document-verification:end -->
          <div class="establishment-footer">
            <div class="footer-line footer-name">${issuerName}</div>
            <div class="footer-line document-reference">${documentReference}</div>
            ${warehouse?.address ? '<div class="footer-line">' + warehouse.address + '</div>' : ""}
            ${footerContacts ? '<div class="footer-line">' + footerContacts + '</div>' : ""}
            ${formatLegalInfo(warehouse)}
            <div class="footer-line">${isProforma ? "Établi" : "Facturé"} par : ${order.created_by_name || "SysAdmin"} ; Imprimé par : ${order.printed_by_name || "N/A"}</div>
          </div>
          </div>
        </footer>
      </div>
      ${!isProforma ? '<script>(' + installInvoicePrintPagination.toString() + ')(' + getInvoicePageLayout.toString() + ');</script>' : ""}
    </body>
    </html>
  `;
};
